/**
 * YÜM WORLD — La Roue Gourmande · rendu <canvas> + animation.
 *
 * Le composant ne DÉCIDE pas du résultat : le parent (WheelPage) obtient
 * le segment gagnant du serveur puis demande à la roue de s'arrêter dessus
 * via `ref.spinTo(index)`. La roue anime un ralenti (ease-out) qui finit
 * pile sous le pointeur, puis appelle `onRest()`.
 *
 * Habillage : parts tournantes + couronne dorée à ampoules (statique).
 * Pointeur, smiley « topper » et moyeu (logo) sont des overlays HTML.
 */
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

const TAU = Math.PI * 2;
const SPIN_MS = 4800;
const MIN_TURNS = 6;
const BULBS = 20;

const easeOutQuart = (t) => 1 - Math.pow(1 - t, 4);

export const Wheel = forwardRef(function Wheel(
  { segments, size = 330, pointerSrc, topperSrc, hubLogoSrc, onRest, onSpinStart },
  ref
) {
  const canvasRef = useRef(null);
  const rotationRef = useRef(0);
  const rafRef = useRef(null);
  const imagesRef = useRef(new Map());
  const [spinning, setSpinning] = useState(false);
  const [pointerErr, setPointerErr] = useState(false);

  const n = segments.length || 1;
  const anglePer = TAU / n;

  useEffect(() => {
    segments.forEach((s) => {
      if (!s.img || imagesRef.current.has(s.img)) return;
      const img = new Image();
      img.src = s.img;
      img.onload = () => {
        imagesRef.current.set(s.img, img);
        draw();
      };
      img.onerror = () => {};
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [segments]);

  function draw() {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dpr = typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1;
    if (canvas.width !== size * dpr) {
      canvas.width = size * dpr;
      canvas.height = size * dpr;
    }
    ctx.save();
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, size, size);

    const cx = size / 2;
    const cy = size / 2;
    const outer = size / 2 - 4;
    const r = size / 2 - 24;

    // ---- Parts tournantes ----
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(rotationRef.current);

    for (let i = 0; i < n; i++) {
      const seg = segments[i];
      const start = i * anglePer;
      const end = start + anglePer;

      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.arc(0, 0, r, start, end);
      ctx.closePath();
      ctx.fillStyle = seg.color;
      ctx.fill();
      ctx.lineWidth = 2;
      ctx.strokeStyle = "rgba(255,255,255,0.6)";
      ctx.stroke();

      const mid = start + anglePer / 2;
      ctx.save();
      ctx.rotate(mid);
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";

      // Icône : image détourée si dispo, sinon emoji (custom ou auto) au centre.
      const img = imagesRef.current.get(seg.img);
      const iconR = r * 0.6;
      const iconSize = r * 0.32;
      ctx.save();
      ctx.translate(iconR, 0);
      ctx.rotate(Math.PI / 2);
      if (img) {
        ctx.drawImage(img, -iconSize / 2, -iconSize / 2, iconSize, iconSize);
      } else {
        ctx.font = `${Math.round(r * 0.26)}px serif`;
        ctx.fillText(seg.emoji || "🎁", 0, 0);
      }
      ctx.restore();

      // Label : texte de l'admin, blanc/foncé selon la part + CONTOUR contrasté
      // (lisible sur toutes les couleurs), rétréci et coupé sur 2 lignes si long.
      ctx.save();
      ctx.translate(r * 0.9, 0);
      ctx.rotate(Math.PI / 2);
      const budget = r * 0.52;
      const setFont = (px) => (ctx.font = `700 ${px}px 'Fredoka', system-ui, sans-serif`);
      let fs = Math.round(r * 0.085);
      const words = (seg.label || "").split(" ");
      setFont(fs);
      let lines = [seg.label];
      if (words.length > 1 && ctx.measureText(seg.label).width > budget) {
        let best = 1, bestDiff = Infinity;
        for (let k = 1; k < words.length; k++) {
          const w1 = ctx.measureText(words.slice(0, k).join(" ")).width;
          const w2 = ctx.measureText(words.slice(k).join(" ")).width;
          if (Math.abs(w1 - w2) < bestDiff) { bestDiff = Math.abs(w1 - w2); best = k; }
        }
        lines = [words.slice(0, best).join(" "), words.slice(best).join(" ")];
      }
      const fits = () => lines.every((l) => ctx.measureText(l).width <= budget);
      while (fs > 8) { setFont(fs); if (fits()) break; fs -= 1; }
      setFont(fs);
      ctx.fillStyle = seg.ink;
      ctx.strokeStyle = seg.ink === "#ffffff" ? "rgba(0,0,0,0.55)" : "rgba(255,255,255,0.75)";
      ctx.lineWidth = Math.max(2.5, fs * 0.16);
      ctx.lineJoin = "round";
      const lh = fs * 1.04;
      const y0 = -((lines.length - 1) * lh) / 2;
      lines.forEach((l, li) => {
        ctx.strokeText(l, 0, y0 + li * lh);
        ctx.fillText(l, 0, y0 + li * lh);
      });
      ctx.restore();

      ctx.restore();
    }
    ctx.restore();

    // ---- Couronne dorée + ampoules (statique) ----
    ctx.save();
    ctx.translate(cx, cy);

    const ringMid = (r + outer) / 2;
    const ringW = outer - r;
    const grad = ctx.createLinearGradient(-outer, -outer, outer, outer);
    grad.addColorStop(0, "#ffe7a0");
    grad.addColorStop(0.45, "#e8a32b");
    grad.addColorStop(0.55, "#c9821a");
    grad.addColorStop(1, "#ffd76a");
    ctx.beginPath();
    ctx.arc(0, 0, ringMid, 0, TAU);
    ctx.lineWidth = ringW;
    ctx.strokeStyle = grad;
    ctx.stroke();

    ctx.lineWidth = 1.5;
    ctx.strokeStyle = "rgba(120,70,0,.35)";
    ctx.beginPath();
    ctx.arc(0, 0, r + 1, 0, TAU);
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(0, 0, outer - 1, 0, TAU);
    ctx.stroke();

    for (let b = 0; b < BULBS; b++) {
      const a = (b / BULBS) * TAU - Math.PI / 2;
      const bx = Math.cos(a) * ringMid;
      const by = Math.sin(a) * ringMid;
      const br = ringW * 0.28;
      const g = ctx.createRadialGradient(bx, by, 0, bx, by, br * 1.8);
      const warm = b % 2 === 0;
      g.addColorStop(0, "#ffffff");
      g.addColorStop(0.4, warm ? "#fff2b0" : "#ffd0e6");
      g.addColorStop(1, "rgba(255,200,80,0)");
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.arc(bx, by, br * 1.8, 0, TAU);
      ctx.fill();
      ctx.fillStyle = warm ? "#fff8d6" : "#ffe3f1";
      ctx.beginPath();
      ctx.arc(bx, by, br, 0, TAU);
      ctx.fill();
    }

    // Disque central (base du moyeu)
    const hubR = r * 0.22;
    ctx.beginPath();
    ctx.arc(0, 0, hubR, 0, TAU);
    ctx.fillStyle = "#fff7ec";
    ctx.fill();
    ctx.lineWidth = hubR * 0.16;
    ctx.strokeStyle = "#e8a32b";
    ctx.stroke();
    if (!hubLogoSrc) {
      ctx.fillStyle = "#e2231a";
      ctx.font = `800 ${Math.round(hubR * 0.5)}px system-ui, sans-serif`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText("YÜM", 0, 0);
    }

    ctx.restore();
    ctx.restore();
  }

  useEffect(() => {
    draw();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [size, segments]);

  useImperativeHandle(ref, () => ({
    spinTo(segmentIndex) {
      if (spinning) return;
      setSpinning(true);
      if (onSpinStart) onSpinStart();

      const centerAngle = segmentIndex * anglePer + anglePer / 2;
      const jitter = (Math.random() - 0.5) * anglePer * 0.6;
      const pointer = -Math.PI / 2;

      const current = rotationRef.current;
      let target = pointer - centerAngle - jitter;
      const base = current + MIN_TURNS * TAU;
      while (target < base) target += TAU;

      const from = current;
      const delta = target - from;
      const startT = typeof performance !== "undefined" ? performance.now() : 0;

      const tick = (now) => {
        const elapsed = now - startT;
        const t = Math.min(1, elapsed / SPIN_MS);
        rotationRef.current = from + delta * easeOutQuart(t);
        draw();
        if (t < 1) {
          rafRef.current = requestAnimationFrame(tick);
        } else {
          rotationRef.current = ((target % TAU) + TAU) % TAU;
          draw();
          setSpinning(false);
          if (onRest) onRest(segmentIndex);
        }
      };
      rafRef.current = requestAnimationFrame(tick);
    },
  }));

  useEffect(
    () => () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    },
    []
  );

  const hubSize = size * 0.34;

  return (
    <div style={{ position: "relative", width: size, height: size, margin: "0 auto" }}>
      {/* Smiley « topper » au sommet */}
      {topperSrc && (
        <img
          src={topperSrc}
          alt=""
          aria-hidden="true"
          style={{
            position: "absolute",
            top: -size * 0.12,
            left: "50%",
            transform: "translateX(-50%)",
            width: size * 0.2,
            height: "auto",
            zIndex: 4,
            filter: "drop-shadow(0 3px 4px rgba(0,0,0,.3))",
          }}
        />
      )}

      {/* Pointeur (image, fallback triangle) */}
      {pointerSrc && !pointerErr ? (
        <img
          src={pointerSrc}
          alt=""
          aria-hidden="true"
          onError={() => setPointerErr(true)}
          style={{
            position: "absolute",
            top: size * 0.01,
            left: "50%",
            transform: "translateX(-50%)",
            width: size * 0.15,
            height: "auto",
            zIndex: 3,
            filter: "drop-shadow(0 3px 3px rgba(0,0,0,.3))",
          }}
        />
      ) : (
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            top: size * 0.04,
            left: "50%",
            transform: "translateX(-50%)",
            width: 0,
            height: 0,
            borderLeft: "16px solid transparent",
            borderRight: "16px solid transparent",
            borderTop: "26px solid #e2231a",
            filter: "drop-shadow(0 2px 2px rgba(0,0,0,.3))",
            zIndex: 3,
          }}
        />
      )}

      <canvas
        ref={canvasRef}
        style={{
          width: size,
          height: size,
          borderRadius: "50%",
          boxShadow: "0 10px 30px rgba(0,0,0,.3)",
          background: "transparent",
        }}
      />

      {/* Moyeu : logo posé sur le disque crème dessiné en canvas */}
      {hubLogoSrc && (
        <img
          src={hubLogoSrc}
          alt="YÜM WORLD"
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            width: hubSize,
            height: "auto",
            transform: "translate(-50%, -50%)",
            zIndex: 2,
            pointerEvents: "none",
          }}
        />
      )}
    </div>
  );
});

export default Wheel;
